import { useCallback, useEffect, useState } from "react";
import { ApiError, apiGet, apiPost } from "../lib/api";
import type {
  DraftReply,
  DraftReplyApiResult,
  ReviewDetailResponse,
  ReviewSummary,
} from "../lib/types";
import { StarRating } from "../components/StarRating";
import { StatusBadge } from "../components/StatusBadge";
import { fullDate, timeAgo } from "../lib/format";

interface Props {
  reviewId: string;
  onMutated: () => void;
}

export function ReviewDetail({ reviewId, onMutated }: Props) {
  const [data, setData] = useState<ReviewDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    "draft" | "approve" | "post" | "skip" | "snooze" | "hide" | null
  >(null);
  const [editedText, setEditedText] = useState<string>("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<ReviewDetailResponse>(`/api/reviews/${reviewId}`);
      setData(res);
      const latest = res.drafts[0];
      setEditedText(latest?.finalText ?? latest?.text ?? "");
    } catch (err) {
      console.error("review detail failed:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <div className="p-10 text-sm text-slate-500">Loading review…</div>;
  }
  if (error || !data) {
    return <div className="p-10 text-sm text-rose-700">{error ?? "Failed to load."}</div>;
  }

  const { review, drafts } = data;
  const latestDraft: DraftReply | null = drafts[0] ?? null;

  const onDraft = async () => {
    setBusy("draft");
    try {
      const result = await apiPost<DraftReplyApiResult>(`/api/reviews/${reviewId}/draft`);
      await load();
      onMutated();
      setEditedText(result.text);
    } catch (err) {
      alert(formatErr(err, "Drafting failed"));
    } finally {
      setBusy(null);
    }
  };

  const onApprove = async () => {
    if (!latestDraft) return;
    if (!editedText.trim()) {
      alert("Reply text is empty.");
      return;
    }
    setBusy("approve");
    try {
      await apiPost(`/api/reviews/${reviewId}/approve`, { draftId: latestDraft.id, finalText: editedText });
      await load();
      onMutated();
    } catch (err) {
      alert(formatErr(err, "Approve failed"));
    } finally {
      setBusy(null);
    }
  };

  const onPost = async () => {
    if (!latestDraft || !latestDraft.finalText) {
      alert("No approved draft to post.");
      return;
    }
    setBusy("post");
    try {
      const result = await apiPost<{ ok: boolean; fixture?: boolean }>(`/api/reviews/${reviewId}/post`, {
        draftId: latestDraft.id,
      });
      await load();
      onMutated();
      if (result.fixture) {
        // soft toast — in real UI this would go through a notification system
        console.info("Posted in fixture mode (no live GBP call).");
      }
    } catch (err) {
      alert(formatErr(err, "Post failed"));
    } finally {
      setBusy(null);
    }
  };

  const onSnooze = async (days: number) => {
    setBusy("snooze");
    try {
      await apiPost(`/api/reviews/${reviewId}/snooze`, { days });
      await load();
      onMutated();
    } catch (err) {
      alert(formatErr(err, "Snooze failed"));
    } finally {
      setBusy(null);
    }
  };

  const onUnsnooze = async () => {
    setBusy("snooze");
    try {
      await apiPost(`/api/reviews/${reviewId}/unsnooze`);
      await load();
      onMutated();
    } catch (err) {
      alert(formatErr(err, "Unsnooze failed"));
    } finally {
      setBusy(null);
    }
  };

  const onHide = async () => {
    if (
      !window.confirm(
        "Hide this review permanently? It will disappear from the queue for everyone until someone unhides it via the Hidden filter.",
      )
    )
      return;
    setBusy("hide");
    try {
      await apiPost(`/api/reviews/${reviewId}/hide`);
      await load();
      onMutated();
    } catch (err) {
      alert(formatErr(err, "Hide failed"));
    } finally {
      setBusy(null);
    }
  };

  const onUnhide = async () => {
    setBusy("hide");
    try {
      await apiPost(`/api/reviews/${reviewId}/unhide`);
      await load();
      onMutated();
    } catch (err) {
      alert(formatErr(err, "Unhide failed"));
    } finally {
      setBusy(null);
    }
  };

  const onSkip = async () => {
    const reason = window.prompt("Reason for skipping? (optional)") ?? undefined;
    setBusy("skip");
    try {
      await apiPost(`/api/reviews/${reviewId}/skip`, { reason });
      await load();
      onMutated();
    } catch (err) {
      alert(formatErr(err, "Skip failed"));
    } finally {
      setBusy(null);
    }
  };

  // Google no longer has this review, so every reply action against it will
  // 404. Warn loudly and take the actions away rather than letting someone
  // write a reply that cannot be delivered.
  const removedAtSource = review.removedAtSource;
  const snoozed = Boolean(
    review.snoozedUntil && new Date(review.snoozedUntil) > new Date(),
  );

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-8 sm:py-8">
      <ReviewHeader review={review} />

      {removedAtSource && (
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5">
          <h2 className="text-sm font-semibold text-amber-900">
            ⚠ This review no longer exists on Google
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-amber-800">
            It was deleted by the reviewer or removed by Google, so it can't be replied to —
            attempting to post would fail. Detected {timeAgo(removedAtSource)}. It has been taken
            off the review queue; nothing further is needed.
          </p>
        </div>
      )}

      {review.hiddenAt && (
        <div className="mt-6 flex items-center justify-between rounded-xl border border-slate-300 bg-slate-100 p-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">
              Hidden from the queue
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Hidden {timeAgo(review.hiddenAt)}. It only appears when the
              Hidden filter is on.
            </p>
          </div>
          <button
            onClick={() => void onUnhide()}
            disabled={busy !== null}
            className="rounded-lg border border-slate-400 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Unhide
          </button>
        </div>
      )}

      {!review.hiddenAt && snoozed && (
        <div className="mt-6 flex items-center justify-between rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div>
            <h2 className="text-sm font-semibold text-amber-900">
              Snoozed until {fullDate(review.snoozedUntil!)}
            </h2>
            <p className="mt-1 text-sm text-amber-800">
              It's out of the default queue and will come back on its own.
            </p>
          </div>
          <button
            onClick={() => void onUnsnooze()}
            disabled={busy !== null}
            className="rounded-lg border border-amber-400 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            Unsnooze
          </button>
        </div>
      )}

      {!removedAtSource &&
        !review.hiddenAt &&
        !snoozed &&
        review.status !== "posted" &&
        review.status !== "skipped" &&
        (review.stars <= 2 || latestDraft?.needsEscalation) && (
          <div className="mt-6 rounded-xl border-2 border-rose-400 bg-rose-50 p-4">
            <h2 className="text-sm font-bold text-rose-900">
              ⚠ {review.stars <= 2 ? `${review.stars}★ review — ` : ""}needs
              attention
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-rose-800">
              This review is excluded from bulk approval. Read it carefully —
              the reply should come from the manager and be approved
              individually before posting.
            </p>
          </div>
        )}

      {review.text ? (
        <blockquote className="mt-6 rounded-xl border border-slate-200 bg-white p-5 text-slate-800">
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{review.text}</p>
        </blockquote>
      ) : (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white px-5 py-4 text-sm italic text-slate-500">
          Reviewer left a star rating with no comment.
        </div>
      )}

      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Reply</h2>
          <div className="flex items-center gap-2">
            {!removedAtSource && review.status !== "skipped" && review.status !== "posted" && (
              <button
                onClick={() => void onDraft()}
                disabled={busy !== null}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {latestDraft ? "Regenerate" : "Draft reply"}
              </button>
            )}
            {!removedAtSource && review.status !== "skipped" && review.status !== "posted" && (
              <button
                onClick={() => void onSkip()}
                disabled={busy !== null}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Skip
              </button>
            )}
            {!removedAtSource && !review.hiddenAt && (
              <select
                value=""
                onChange={(e) => {
                  const days = Number(e.target.value);
                  if (days > 0) void onSnooze(days);
                }}
                disabled={busy !== null}
                className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                <option value="">{snoozed ? "Re-snooze…" : "Snooze…"}</option>
                <option value="1">1 day</option>
                <option value="3">3 days</option>
                <option value="7">1 week</option>
                <option value="14">2 weeks</option>
                <option value="30">1 month</option>
              </select>
            )}
            {!removedAtSource && !review.hiddenAt && (
              <button
                onClick={() => void onHide()}
                disabled={busy !== null}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                title="Remove from the queue permanently (reversible via the Hidden filter)"
              >
                Hide
              </button>
            )}
          </div>
        </div>

        {!latestDraft && (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white px-5 py-6 text-center text-sm text-slate-500">
            No draft yet. Click <span className="font-semibold text-slate-700">Draft reply</span> to generate one with Gemini.
          </div>
        )}

        {latestDraft && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <div className="flex items-center gap-3">
                <span>{latestDraft.modelId}</span>
                {latestDraft.confidence !== null && (
                  <span>
                    confidence
                    <span
                      className={`ml-1 inline-flex h-1.5 w-12 overflow-hidden rounded-full bg-slate-200 align-middle`}
                    >
                      <span
                        className={`h-full ${
                          latestDraft.confidence >= 85
                            ? "bg-emerald-500"
                            : latestDraft.confidence >= 70
                              ? "bg-amber-500"
                              : "bg-rose-500"
                        }`}
                        style={{ width: `${latestDraft.confidence}%` }}
                      />
                    </span>
                    <span className="ml-1">{latestDraft.confidence}</span>
                  </span>
                )}
                {latestDraft.needsEscalation && (
                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-700 ring-1 ring-inset ring-rose-200">
                    ⚑ escalation
                  </span>
                )}
              </div>
              <span>generated {timeAgo(latestDraft.createdAt)}</span>
            </div>
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              rows={Math.min(12, Math.max(6, Math.ceil(editedText.length / 60)))}
              className="mt-3 block w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
              disabled={review.status === "posted"}
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500">
                {latestDraft.approvedAt && (
                  <span className="mr-3">
                    approved {timeAgo(latestDraft.approvedAt)}
                  </span>
                )}
                {latestDraft.postedAt && (
                  <span>posted {timeAgo(latestDraft.postedAt)}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!removedAtSource && review.status !== "posted" && (
                  <button
                    onClick={() => void onApprove()}
                    disabled={busy !== null}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                  >
                    {review.status === "ready" ? "Re-approve" : "Approve edits"}
                  </button>
                )}
                {!removedAtSource && review.status === "ready" && (
                  <button
                    onClick={() => void onPost()}
                    disabled={busy !== null || !latestDraft.finalText}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Post reply
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {drafts.length > 1 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-900">
              {drafts.length - 1} previous draft{drafts.length === 2 ? "" : "s"}
            </summary>
            <ul className="mt-2 space-y-2">
              {drafts.slice(1).map((d) => (
                <li
                  key={d.id}
                  className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600"
                >
                  <div className="flex items-center justify-between text-slate-400">
                    <span>{d.modelId}</span>
                    <span>{timeAgo(d.createdAt)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap font-mono">{d.text}</p>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </div>
  );
}

function ReviewHeader({ review }: { review: ReviewSummary }) {
  return (
    <header>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StarRating stars={review.stars} />
          <span className="text-sm font-medium text-slate-900">
            {review.reviewerName ?? "Anonymous"}
          </span>
        </div>
        <StatusBadge status={review.status} />
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
        <span title={fullDate(review.postedAt)}>{fullDate(review.postedAt)}</span>
        <span>·</span>
        <span className="uppercase">{review.source}</span>
        {review.sentiment && (
          <>
            <span>·</span>
            <span className="capitalize">{review.sentiment}</span>
          </>
        )}
      </div>
    </header>
  );
}

function formatErr(err: unknown, prefix: string): string {
  if (err instanceof ApiError) {
    // Routes that can fail for a reason the user can act on send a human-readable
    // `message` alongside the machine `error` code. Prefer it — surfacing the bare
    // code is what turned "this review was deleted on Google" into the useless
    // "Post failed: internal_error (HTTP 500)".
    const body = err.body;
    if (typeof body === "object" && body !== null && "message" in body) {
      const msg = (body as { message: unknown }).message;
      if (typeof msg === "string" && msg.trim()) return `${prefix}: ${msg}`;
    }
    return `${prefix}: ${err.message} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return `${prefix}: ${err.message}`;
  return `${prefix}: ${String(err)}`;
}
